/**
 * modal.ts - モーダル/ウィンドウ共通スタイル
 *
 * NineSliceWindow, 各種モーダルで使用する共通スタイル定義
 */

import { CSSProperties } from 'react';
import { colors, spacing, borderRadius, shadows, zIndex, typography, transitions } from '../theme';

// ========================================
// オーバーレイスタイル
// ========================================

export const overlayStyles: Record<string, CSSProperties> = {
  /** 標準オーバーレイ（暗め） */
  default: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: colors.background.overlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: zIndex.modal,
  },

  /** 軽めのオーバーレイ */
  light: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: colors.background.overlayLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: zIndex.modal,
  },
};

// ========================================
// モーダル内部コンテナスタイル
// ========================================

export const modalContentStyles: Record<string, CSSProperties> = {
  /** フレックスコンテナ（縦並び） */
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },

  /** ヘッダー（タイトル + 閉じるボタン） */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: spacing.md,
    borderBottom: `1px solid ${colors.border.light}`,
    marginBottom: spacing.md,
  },

  /** タイトル */
  title: {
    color: colors.text.primary,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
  },

  /** 閉じるボタン */
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: colors.text.secondary,
    fontSize: typography.fontSize.xl,
    cursor: 'pointer',
    padding: `${spacing.xs}px ${spacing.sm}px`,
    transition: transitions.normal,
  },

  /** スクロール可能なコンテンツエリア */
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },

  /** フッター（ボタン配置用） */
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    paddingTop: spacing.md,
    borderTop: `1px solid ${colors.border.light}`,
    marginTop: spacing.md,
  },
};

// ========================================
// フォールバックモーダルスタイル
// （9スライス画像がない場合に使用）
// ========================================

export const fallbackModalStyles = {
  /** 標準（紫アクセント） */
  default: {
    background: `linear-gradient(135deg, ${colors.background.primary} 0%, ${colors.background.secondary} 100%)`,
    border: `2px solid ${colors.accent.purple}`,
    boxShadow: shadows.glow.purple,
    borderRadius: borderRadius.lg,
  },

  /** バトル画面用 */
  battle: {
    background: `linear-gradient(135deg, ${colors.background.primary} 0%, ${colors.background.card} 100%)`,
    border: `3px solid ${colors.accent.purpleLight}`,
    boxShadow: shadows.glow.purpleStrong,
    borderRadius: borderRadius.xl,
  },

  /** ショップ用（緑アクセント） */
  shop: {
    background: `linear-gradient(135deg, #1a2a1a 0%, #2a3a2a 100%)`,
    border: `2px solid ${colors.accent.green}`,
    boxShadow: shadows.glow.green,
    borderRadius: borderRadius.lg,
  },

  /** メニュー用（グレー） */
  menu: {
    background: `linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)`,
    border: `2px solid ${colors.border.default}`,
    boxShadow: shadows.lg,
    borderRadius: borderRadius.lg,
  },

  /** 重要ダイアログ（ゴールド） */
  dialog: {
    background: `linear-gradient(135deg, ${colors.background.primary} 0%, ${colors.background.secondary} 100%)`,
    border: `2px solid ${colors.accent.gold}`,
    boxShadow: shadows.glow.gold,
    borderRadius: borderRadius.lg,
  },

  /** ツールチップ */
  tooltip: {
    background: 'rgba(20, 20, 40, 0.95)',
    border: `1px solid ${colors.border.default}`,
    boxShadow: shadows.md,
    borderRadius: borderRadius.md,
  },
} as const;

// ========================================
// タブスタイル
// ========================================

export const tabStyles: Record<string, CSSProperties> = {
  /** タブコンテナ */
  container: {
    display: 'flex',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },

  /** タブ基本スタイル */
  tab: {
    padding: `${spacing.sm}px ${spacing.lg}px`,
    border: 'none',
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
    transition: transitions.normal,
  },

  /** アクティブタブ */
  active: {
    backgroundColor: colors.accent.blue,
    color: colors.text.primary,
  },

  /** 非アクティブタブ */
  inactive: {
    backgroundColor: colors.border.default,
    color: colors.text.secondary,
  },
};

// ========================================
// 型定義
// ========================================

export type FallbackModalVariant = keyof typeof fallbackModalStyles;
